import { Input, TextArea, Button, Tooltip } from '@dsi/react-eds';
import { Controller } from 'react-hook-form';
import { AxiosError } from 'axios';
import ReactEDSWrapper from '../../../../utils/react-eds-wrapper';
import { useLoader } from '../../../../hooks/useLoader';
import { useSwobNotifications } from '../../../../hooks/NotificationsProvider';
import { cnisConfigurationService } from '../../../../services/cnisConfigurationService';
import { useCnisConfigForm } from './cnis-config-form';
import styles from './CnisConfiguration.module.css';

function CnisConfigurationForm() {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty }
  } = useCnisConfigForm();
  const { showLoader, hideLoader } = useLoader();
  const { addNotifications } = useSwobNotifications();

  const onSubmit = handleSubmit(async (data) => {
    showLoader();
    try {
      const { ontap, ...rest } = data;
      await cnisConfigurationService.create({
        ...rest,
        ...(ontap ? { ontap: { name: 'ontap', version: ontap } } : {})
      });
      reset();
      addNotifications([
        {
          title: 'CNIS Configuration Saved',
          icon: 'icon-check',
          description: 'Configuration saved successfully.',
          color: 'green',
          timestamp: new Date()
        }
      ]);
    } catch (error) {
      addNotifications([
        {
          title: 'Error - CNIS Configuration',
          icon: 'icon-triangle-warning',
          description:
            error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while saving the configuration.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
    } finally {
      setTimeout(() => {
        hideLoader();
      }, 500);
    }
  });

  return (
    <>
      <div className={styles.settingsTitle}>Release Notes</div>
      <p className={styles.subtitle}>Provide the Release Name, NetApp ONTAP version and add the list of ticket numbers.</p>
      <p className={styles.subtitle}> </p>
      <form onSubmit={onSubmit}>
        <div className={`row ${styles.fieldCnisConfigRow}`}>
          <div className="column sm-12">
            <Controller
              name="release"
              control={control}
              render={({ field }) => (
                <ReactEDSWrapper
                  component={Input}
                  id="release-name"
                  label="Release Name"
                  type="text"
                  placeholder="Release Name"
                  required
                  {...field}
                />
              )}
            />
            {errors.release && <span className={styles.error}>&#9888; {errors.release.message}</span>}
          </div>
        </div>
        <div className={`row ${styles.fieldCnisConfigRow}`}>
          <div className="column sm-12">
            <Controller
              name="ontap"
              control={control}
              render={({ field }) => (
                <ReactEDSWrapper
                  component={Input}
                  id="ontap-version"
                  type="text"
                  label="ONTAP Version"
                  placeholder="ONTAP Version"
                  {...field}
                />
              )}
            />
            {errors.ontap && <span className={styles.error}>&#9888; {errors.ontap.message}</span>}
          </div>
        </div>
        <div className={`row ${styles.fieldCnisConfigRow}`}>
          <div className="column sm-12">
            <label htmlFor="release-tickets">
              Release Ticket Numbers <span className={styles.requiredAsterisk}> *</span>
              <ReactEDSWrapper component={Tooltip} message="Enter the ticket numbers separated by commas" pointer>
                <span>
                  <i className="icon icon-info"></i>
                </span>
              </ReactEDSWrapper>
            </label>
            <Controller
              name="tickets"
              control={control}
              render={({ field }) => (
                <ReactEDSWrapper component={TextArea} id="release-tickets" placeholder="Release Ticket Numbers" rows={6} {...field} />
              )}
            />
            {errors.tickets && <span className={styles.error}>&#9888; {errors.tickets.message}</span>}
          </div>
        </div>
        <div className="row">
          <div className="column sm-12">
            <div className={styles.buttons}>
              <ReactEDSWrapper component={Button} type="button" onClick={() => reset()}>
                Cancel
              </ReactEDSWrapper>
              <ReactEDSWrapper component={Button} primary type="submit" disabled={!isDirty}>
                Save
              </ReactEDSWrapper>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}
export default CnisConfigurationForm;
